import Cookies from "js-cookie";
import useRequest from "~/hooks/use-request";
import type { CardTokenParams, CardTokenUpdateParams, InstanceMercadoPago } from "~/modules/mercadopago/module";
import useUserStore from "~/stores/user";
import {
  formatCreditCardInfo,
  formatOrderItem,
  formatPaginatedResponseItems,
  formatRedeemAccessCode,
} from "./format";

const updateStoredCreditCard = (card: IModelCreditCard | null) => {
  const userStore = useUserStore();
  userStore.creditCard = card;
};

interface IRequestPurchaseOptions {
  productId: string;
  token: string;
  saveAsDefault?: boolean;
}

const BillingAPI = {
  load(): Promise<IModelCreditCard | null> {
    const userStore = useUserStore();

    if (userStore.creditCard)
      return Promise.resolve(userStore.creditCard);

    const { request } = useRequest();
    return request
      .get<IContentResponse<IResponseCreditCardInfo>>("/api/payment/user_method_info")
      .then((response) => {
        if (Object.keys(response.content).length !== 0) {
          userStore.creditCard = formatCreditCardInfo(response.content);
          return formatCreditCardInfo(response.content);
        }
        return null;
      });
  },

  loadHistory(options: IRequestBillingHistoryParams) {
    const { request } = useRequest();

    return request
      .get<IPaginatedResponse<IResponseBillingHistory>>("/api/commerce/billing_history", { query: options })
      .then((response) => {
        const formattedBillingHistory = formatPaginatedResponseItems(response, formatOrderItem);

        return formattedBillingHistory;
      });
  },

  update(tokenID: string): Promise<void> {
    const { request } = useRequest();
    const payload = {
      card: { token: tokenID },
    };

    return request
      .post<IContentResponse<IResponseCreditCardInfo>>("/api/commerce/set_billing_info", { body: payload })
      .then(response => updateStoredCreditCard(formatCreditCardInfo(response.content)));
  },

  delete(): Promise<void> {
    const { request } = useRequest();
    const userStore = useUserStore();
    const payload = { card_id: userStore.creditCard?.id, user_id: userStore.user?.id };

    return request
      .post("/api/payment/delete_method", { body: payload })
      .then(() => updateStoredCreditCard(null));
  },

  redeemAccessCode(code: string): Promise<IModelCommerce> {
    const { request } = useRequest();

    return request
      .post<IContentResponse<IResponseReedemCode>>("/api/commerce/redeem_code", { body: { code } })
      .then((response) => {
        return formatRedeemAccessCode(response.content);
      });
  },

  purchase(options: IRequestPurchaseOptions) {
    const { request } = useRequest();

    return request
      .post("/api/payment/purchase", { body: options });
  },

  async createMercadoPagoCardToken(card: CardTokenParams): Promise<string> {
    const nuxtApp = useNuxtApp();
    const userStore = useUserStore();
    const mercadopago = nuxtApp.$mercadopago as InstanceMercadoPago;

    try {
      const response = await mercadopago?.createCardToken(card);
      if (!response)
        throw new Error("ERR400GN00"); // Error ("Bad Request")

      userStore.creditCardToken = response.id;

      return response.id;
    }
    catch (error: any) {
      throw new Error(error.message);
    }
  },

  async updateMercadoPagoCardToken(params: CardTokenUpdateParams): Promise<string> {
    const nuxtApp = useNuxtApp();
    const userStore = useUserStore();
    const mercadopago = nuxtApp.$mercadopago as InstanceMercadoPago;

    try {
      const response = await mercadopago?.updateCardToken(params);

      if (!response)
        throw new Error("ERR400GN00"); // Error ("Bad Request")

      userStore.creditCardToken = response.id;

      return response.id;
    }
    catch (error: any) {
      throw new Error(error.message);
    }
  },

  async payWithMercadoPagoWithUpdatedToken(subscriptionId: string, options: CardTokenUpdateParams): Promise<void> {
    try {
      const tokenID = await BillingAPI.updateMercadoPagoCardToken(options);
      await BillingAPI.purchase({ productId: subscriptionId, token: tokenID });
    }
    catch (error: any) {
      throw new Error(error.message);
    }
  },

  async payWithMercadoPagoCreditCard(subscriptionId: string, card: CardTokenParams): Promise<void> {
    try {
      const tokenID = await BillingAPI.createMercadoPagoCardToken(card);
      await BillingAPI.purchase({ productId: subscriptionId, token: tokenID });
    }
    catch (error: any) {
      throw new Error(error.message);
    }
  },

  initPayment(productId: string, callbackUrl: string): Promise<void> {
    const { request } = useRequest();
    const payload = { productId, callbackUrl };

    return request
      .post<IContentResponse<IResponseInitPayment>>("/api/payment/initialize", { body: payload })
      .then((response) => {
        Cookies.set(`transactionId_${productId}`, response.content.transactionId, {
          expires: new Date(new Date().getTime() + 5 * 60 * 1000), // 5 minutes
          sameSite: "strict",
        });
        return response.content.authorizationUrl;
      })
      .then((url) => {
        window.location.href = url;
      });
  },

  verifyTransaction(transactionId: string): Promise<void> {
    const { request } = useRequest();
    const payload = { transactionId };

    return request
      .post("/api/payment/verify", { body: payload });
  },
};

export default BillingAPI;
